import { useState } from 'react';
import {
  Tabs, Tab, Box, Button, Stack, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Alert, Snackbar,
} from '@mui/material';
import { Add, Sync } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { revenueApi, costApi, type RevenueEntry, type CostEntry } from '../../api/profitability.api';
import { omsApi } from '../../api/oms.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

const REVENUE_TYPES = ['boq', 'variation', 'additional_wo', 'other'];
const COST_TYPES = ['material', 'labour', 'logistics', 'travel', 'accommodation', 'miscellaneous', 'rework'];
const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN')}`;

export function ProfitEntriesPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [tab, setTab] = useState(0);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<{ omsId: number | ''; type: string; amount: number; entryDate: string; narration: string }>({ omsId: '', type: 'boq', amount: 0, entryDate: '', narration: '' });
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const isRevenue = tab === 0;
  const revenues = useQuery({ queryKey: ['revenue-entries'], queryFn: () => revenueApi.list({ limit: 50 }), enabled: isRevenue });
  const costs = useQuery({ queryKey: ['cost-entries'], queryFn: () => costApi.list({ limit: 50 }), enabled: !isRevenue });
  const omsList = useQuery({ queryKey: ['oms', 'forEntries'], queryFn: () => omsApi.list({ limit: 100 }), enabled: open });
  const invalidate = () => { qc.invalidateQueries({ queryKey: ['revenue-entries'] }); qc.invalidateQueries({ queryKey: ['cost-entries'] }); };

  const create = useMutation({
    mutationFn: async () => {
      const base = { omsId: Number(form.omsId), amount: form.amount, entryDate: form.entryDate || undefined, narration: form.narration || undefined };
      if (isRevenue) await revenueApi.create({ ...base, revenueType: form.type });
      else await costApi.create({ ...base, costType: form.type });
    },
    onSuccess: () => { setOpen(false); setForm({ omsId: '', type: isRevenue ? 'boq' : 'material', amount: 0, entryDate: '', narration: '' }); invalidate(); },
    onError: (e) => setError(extractApiError(e)),
  });
  const syncBoq = useMutation({ mutationFn: () => revenueApi.syncBoq(), onSuccess: (r) => { setToast(`Synced ${r.synced} BOQ revenue entries`); invalidate(); } });
  const syncProc = useMutation({ mutationFn: () => costApi.syncProcurement(), onSuccess: (r) => { setToast(`Synced ${r.material} PO + ${r.labour} WO cost entries`); invalidate(); } });

  const revCols: Column<RevenueEntry>[] = [
    { key: 'omsId', label: 'OMS', width: 80 },
    { key: 'revenueType', label: 'Type' },
    { key: 'amount', label: 'Amount', align: 'right', render: (r) => inr(r.amount) },
    { key: 'entryDate', label: 'Date' },
    { key: 'narration', label: 'Narration' },
  ];
  const costCols: Column<CostEntry>[] = [
    { key: 'omsId', label: 'OMS', width: 80 },
    { key: 'costType', label: 'Category' },
    { key: 'amount', label: 'Amount', align: 'right', render: (r) => inr(r.amount) },
    { key: 'source', label: 'Source' },
    { key: 'entryDate', label: 'Date' },
  ];

  const openNew = () => { setForm({ omsId: '', type: isRevenue ? 'boq' : 'material', amount: 0, entryDate: '', narration: '' }); setError(null); setOpen(true); };
  const canCreate = hasPermission(isRevenue ? 'revenue.create' : 'cost.create');

  return (
    <>
      <PageHeader
        title="Revenue & Cost Entries"
        subtitle="The ledger that powers profitability — manual entries plus sync from BOQ / procurement."
        action={
          <Stack direction="row" spacing={1}>
            {isRevenue && hasPermission('revenue.create') && <Button startIcon={<Sync />} variant="outlined" onClick={() => syncBoq.mutate()}>Sync BOQ</Button>}
            {!isRevenue && hasPermission('cost.create') && <Button startIcon={<Sync />} variant="outlined" onClick={() => syncProc.mutate()}>Sync Procurement</Button>}
            {canCreate && <Button startIcon={<Add />} variant="contained" onClick={openNew}>New Entry</Button>}
          </Stack>
        }
      />
      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}><Tab label="Revenue" /><Tab label="Cost" /></Tabs>

      {isRevenue ? (
        <DataTable columns={revCols} rows={revenues.data?.data ?? []} loading={revenues.isLoading} page={1} limit={50} total={revenues.data?.meta?.total ?? 0} onPageChange={() => undefined} onLimitChange={() => undefined} rowKey={(r) => r.id} emptyTitle="No revenue entries" />
      ) : (
        <DataTable columns={costCols} rows={costs.data?.data ?? []} loading={costs.isLoading} page={1} limit={50} total={costs.data?.meta?.total ?? 0} onPageChange={() => undefined} onLimitChange={() => undefined} rowKey={(r) => r.id} emptyTitle="No cost entries" />
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>New {isRevenue ? 'Revenue' : 'Cost'} Entry</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField select label="OMS" value={form.omsId} onChange={(e) => setForm((f) => ({ ...f, omsId: Number(e.target.value) }))} fullWidth>
              {(omsList.data?.data ?? []).map((o) => <MenuItem key={o.id} value={o.id}>{o.omsNumber} — {o.title}</MenuItem>)}
            </TextField>
            <Stack direction="row" spacing={2}>
              <TextField select label={isRevenue ? 'Revenue Type' : 'Cost Category'} value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))} fullWidth>
                {(isRevenue ? REVENUE_TYPES : COST_TYPES).map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
              <TextField label="Amount" type="number" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: Number(e.target.value) }))} fullWidth />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Entry Date" type="date" InputLabelProps={{ shrink: true }} value={form.entryDate} onChange={(e) => setForm((f) => ({ ...f, entryDate: e.target.value }))} fullWidth />
              <TextField label="Narration" value={form.narration} onChange={(e) => setForm((f) => ({ ...f, narration: e.target.value }))} fullWidth />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setOpen(false)}>Cancel</Button><Button variant="contained" disabled={!form.omsId || !form.amount} onClick={() => create.mutate()}>Save</Button></DialogActions>
      </Dialog>

      <Snackbar open={Boolean(toast)} autoHideDuration={6000} onClose={() => setToast(null)}><Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
      <Box />
    </>
  );
}
